import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, XCircle, CreditCard } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";

/**
 * Renders the approval/payment/status action panels for the customer-facing
 * Change Order view. Owns its own processing + approverName state.
 */
export function COStatusActions({ changeOrder, onRefresh }) {
  const [approverName, setApproverName] = useState("");
  const [processing, setProcessing] = useState(false);

  const isApproved = changeOrder.status === "approved" || changeOrder.status === "paid";
  const isRejected = changeOrder.status === "rejected";
  const isPaid = changeOrder.payment_status === "paid";
  const needsApproval = changeOrder.requires_approval && changeOrder.status === "sent";
  const canPay = (isApproved || !changeOrder.requires_approval) && !isPaid && changeOrder.subtotal > 0;
  const totalWithFee = Math.floor(changeOrder.subtotal * 1.03 * 100) / 100;

  const handleApprove = async () => {
    if (!approverName.trim()) { toast.error("Please enter your name to approve"); return; }
    setProcessing(true);
    try {
      await axios.post(`${API}/change-orders/${changeOrder.id}/approve`, { approved_by: approverName });
      toast.success("Change order approved!");
      onRefresh();
    } catch {
      toast.error("Failed to approve change order");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/change-orders/${changeOrder.id}/reject`);
      toast.success("Change order rejected");
      onRefresh();
    } catch {
      toast.error("Failed to reject change order");
    } finally {
      setProcessing(false);
    }
  };

  const handlePayment = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/change-orders/${changeOrder.id}/payment/create-session`, {
        origin_url: window.location.origin,
      });
      window.location.href = response.data.checkout_url;
    } catch {
      toast.error("Failed to initiate payment");
      setProcessing(false);
    }
  };

  return (
    <>
      {needsApproval && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
          <h3 className="font-semibold text-amber-900 mb-4">Approval Required</h3>
          <p className="text-amber-800 text-sm mb-4">
            Please review the changes above and enter your name to approve this change order.
          </p>
          <div className="space-y-4">
            <div>
              <Label htmlFor="approver-name" className="text-amber-800">Your Full Name</Label>
              <Input
                id="approver-name"
                value={approverName}
                onChange={(e) => setApproverName(e.target.value)}
                placeholder="Enter your full name"
                className="mt-1"
                data-testid="approver-name-input"
              />
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleApprove}
                disabled={processing || !approverName.trim()}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                data-testid="approve-co-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {processing ? "Processing..." : "Approve Change Order"}
              </Button>
              <Button
                onClick={handleReject}
                disabled={processing}
                variant="outline"
                className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                data-testid="reject-co-btn"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Reject
              </Button>
            </div>
          </div>
        </div>
      )}

      {canPay && (
        <div className="bg-[#00341d]/5 border border-[#00341d]/20 rounded-lg p-6">
          <h3 className="font-semibold text-[#00341d] mb-4">Payment</h3>
          <p className="text-zinc-600 text-sm mb-4">
            Click below to pay for this change order securely via credit card.
          </p>
          <Button
            onClick={handlePayment}
            disabled={processing}
            className="w-full bg-[#d97706] hover:bg-[#b45309] text-white py-6 text-lg"
            data-testid="pay-co-btn"
          >
            <CreditCard className="w-5 h-5 mr-2" />
            {processing ? "Processing..." : `Pay $${totalWithFee.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
          </Button>
          <p className="text-xs text-zinc-500 text-center mt-3">
            Cash or check payments do not include the 3% processing fee.
          </p>
        </div>
      )}

      {isPaid && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
          <h3 className="font-semibold text-green-900 mb-2">Payment Complete</h3>
          <p className="text-green-700 text-sm">
            This change order has been paid. Thank you for your business!
          </p>
        </div>
      )}

      {isRejected && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <XCircle className="w-12 h-12 text-red-600 mx-auto mb-3" />
          <h3 className="font-semibold text-red-900 mb-2">Change Order Rejected</h3>
          <p className="text-red-700 text-sm">
            This change order has been rejected. Please contact us if you have any questions.
          </p>
        </div>
      )}
    </>
  );
}
